/**
 * Prediction API Service
 * Provides methods to fetch predictions from multiple model types (Foundation, Base, Per-Asset)
 * and SHAP explanations for model interpretability
 */

import { get, post } from './api'
import { ENDPOINTS } from './endpoints'
import { getUseMockData } from '@/contexts/SettingsContext'

// Sensor data record type
export interface SensorDataRecord {
  [key: string]: number | string | null
}

// Data query response
interface DataQueryResponse {
  data: SensorDataRecord[]
  total_count: number
  returned_count: number
}

// Model types available in the system
export type ModelType = 'foundation' | 'base' | 'per_asset'

// Single model prediction response
export interface ModelPrediction {
  model_type: ModelType
  model_name: string
  prediction: number
  confidence: number
  health_score: number
  anomaly_score: number
  timestamp: string
  training_info?: {
    data_points: number
    last_trained: string
    accuracy: number
  }
}

// Unified prediction response (all 3 models)
export interface UnifiedPrediction {
  asset_id: string
  timestamp: string
  predictions: {
    foundation: ModelPrediction
    base: ModelPrediction
    per_asset?: ModelPrediction
  }
  consensus: {
    health_score: number
    confidence: number
    agreement_level: 'high' | 'medium' | 'low'
    recommendation: string
  }
  model_comparison: {
    best_model: ModelType
    confidence_spread: number
    divergence_warning: boolean
  }
}

// SHAP feature explanation
export interface SHAPFeature {
  feature: string
  value: number
  contribution: number
  direction: 'positive' | 'negative'
  importance_rank: number
  baseline_value?: number
}

// SHAP explanation response
export interface SHAPExplanation {
  asset_id: string
  model_type: ModelType
  prediction_value: number
  base_value: number
  features: SHAPFeature[]
  summary: {
    top_positive: SHAPFeature[]
    top_negative: SHAPFeature[]
    explanation_text: string
  }
  timestamp: string
}

// Feature importance response
export interface FeatureImportance {
  feature: string
  importance: number
  category: 'temperature' | 'pressure' | 'vibration' | 'flow' | 'electrical' | 'other'
  trend: 'stable' | 'increasing' | 'decreasing'
  threshold?: {
    warning: number
    critical: number
    current_value: number
  }
}

// Forecast point with confidence intervals
export interface ForecastPoint {
  timestamp: string
  predicted_health: number
  lower_bound: number
  upper_bound: number
  model_type: ModelType
}

// Health forecast response
export interface HealthForecast {
  asset_id: string
  current_health: number
  forecast: ForecastPoint[]
  time_to_threshold?: {
    hours: number
    threshold_type: 'warning' | 'critical'
  }
}

/**
 * Fetch latest sensor data for an asset
 * Returns the most recent sensor readings to use for predictions
 */
export async function getLatestSensorData(assetId: string, limit: number = 1): Promise<SensorDataRecord[]> {
  try {
    const response = await post<DataQueryResponse>(ENDPOINTS.DATA_QUERY(assetId), {
      sort_by: 'event_timestamp',
      ascending: false,
      limit: limit,
      offset: 0
    })
    return response.data
  } catch (error) {
    console.warn('Failed to fetch sensor data, using sample data:', error)
    // Return sample sensor data for prediction when real data unavailable
    return [{
      discharge_temp: 85.2,
      suction_temp: 45.3,
      discharge_pressure: 180.2,
      suction_pressure: 48.5,
      oil_temp: 62.1,
      vibration_amplitude: 2.1,
      motor_current: 125.5,
      compressor_speed: 3600,
      gas_flow_rate: 1250.5,
      ambient_temp: 28.5
    }]
  }
}

/**
 * Get prediction from a specific model type
 */
export async function getPrediction(
  assetId: string,
  modelType: ModelType = 'base'
): Promise<ModelPrediction> {
  return get<ModelPrediction>(`${ENDPOINTS.PREDICT_ASSET(assetId)}?model_type=${modelType}`)
}

/**
 * Get unified prediction from all available models
 * Note: Backend requires POST method with sensor data array
 */
export async function getUnifiedPrediction(assetId: string): Promise<UnifiedPrediction> {
  // Fetch latest sensor data for the asset
  const sensorData = await getLatestSensorData(assetId)

  // Send prediction request with sensor data
  return post<UnifiedPrediction>(ENDPOINTS.PREDICT_UNIFIED(assetId), {
    data: sensorData,
    include_explanations: false,
    include_confidence: true
  })
}

/**
 * Get predictions from all model types in parallel
 */
export async function getAllModelPredictions(assetId: string): Promise<{
  foundation: ModelPrediction | null
  base: ModelPrediction | null
  perAsset: ModelPrediction | null
}> {
  const results = await Promise.allSettled([
    getPrediction(assetId, 'foundation'),
    getPrediction(assetId, 'base'),
    getPrediction(assetId, 'per_asset')
  ])

  return {
    foundation: results[0].status === 'fulfilled' ? results[0].value : null,
    base: results[1].status === 'fulfilled' ? results[1].value : null,
    perAsset: results[2].status === 'fulfilled' ? results[2].value : null
  }
}

/**
 * Get SHAP explanation for a prediction
 * Note: Backend requires POST method with sensor data array
 */
export async function getSHAPExplanation(
  assetId: string,
  _modelType: ModelType = 'base'
): Promise<SHAPExplanation> {
  // Fetch latest sensor data for the asset
  const sensorData = await getLatestSensorData(assetId)

  // Send SHAP explanation request with sensor data
  return post<SHAPExplanation>(ENDPOINTS.EXPLAIN_SHAP(assetId), {
    data: sensorData,
    include_explanations: true,
    include_confidence: true
  })
}

/**
 * Get feature importance for an asset
 */
export async function getFeatureImportance(assetId: string): Promise<FeatureImportance[]> {
  return get<FeatureImportance[]>(ENDPOINTS.EXPLAIN_FEATURE_IMPORTANCE(assetId))
}

/**
 * Get health forecast with confidence intervals
 * Note: Backend requires POST method with metric, steps, confidence_level
 */
export async function getHealthForecast(
  assetId: string,
  steps: number = 7,
  metric: string = 'anomaly_score',
  confidenceLevel: number = 0.95
): Promise<HealthForecast> {
  return post<HealthForecast>(ENDPOINTS.PREDICT_FORECAST(assetId), {
    metric: metric,
    steps: steps,
    confidence_level: confidenceLevel
  })
}

/**
 * Submit prediction feedback for model improvement
 */
export async function submitPredictionFeedback(feedback: {
  asset_id: string
  prediction_id: string
  actual_outcome: string
  model_type: ModelType
  was_accurate: boolean
  notes?: string
}): Promise<{ success: boolean; feedback_id: string }> {
  return post(ENDPOINTS.PREDICT_FEEDBACK, feedback)
}

// Mock data generators for fallback
function generateMockModelPrediction(assetId: string, modelType: ModelType): ModelPrediction {
  const baseHealth = 75 + Math.random() * 20
  const variance = modelType === 'foundation' ? 5 : modelType === 'base' ? 3 : 2

  return {
    model_type: modelType,
    model_name: modelType === 'foundation'
      ? 'Foundation Model v2.1 (Zero-Shot)'
      : modelType === 'base'
        ? 'Base Model v1.5 (All Assets)'
        : `Per-Asset Model (${assetId})`,
    prediction: Math.round((baseHealth + (Math.random() - 0.5) * variance) * 10) / 10,
    confidence: modelType === 'per_asset' ? 0.92 : modelType === 'base' ? 0.85 : 0.78,
    health_score: baseHealth,
    anomaly_score: Math.random() * 0.3,
    timestamp: new Date().toISOString(),
    training_info: modelType === 'per_asset' ? {
      data_points: 15000,
      last_trained: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      accuracy: 0.94
    } : undefined
  }
}

function generateMockSHAPExplanation(assetId: string): SHAPExplanation {
  const features: SHAPFeature[] = [
    { feature: 'Discharge Temperature', value: 85.2, contribution: 0.15, direction: 'negative', importance_rank: 1, baseline_value: 78 },
    { feature: 'Oil Temperature', value: 62.1, contribution: 0.08, direction: 'negative', importance_rank: 2, baseline_value: 55 },
    { feature: 'Suction Pressure', value: 45.3, contribution: -0.05, direction: 'positive', importance_rank: 3, baseline_value: 48 },
    { feature: 'Vibration Amplitude', value: 2.1, contribution: 0.12, direction: 'negative', importance_rank: 4, baseline_value: 1.5 },
    { feature: 'Motor Current', value: 125.5, contribution: -0.03, direction: 'positive', importance_rank: 5, baseline_value: 130 },
    { feature: 'Discharge Pressure', value: 180.2, contribution: 0.02, direction: 'negative', importance_rank: 6, baseline_value: 175 },
    { feature: 'Operating Hours', value: 8760, contribution: 0.06, direction: 'negative', importance_rank: 7, baseline_value: 5000 },
    { feature: 'Ambient Temperature', value: 28.5, contribution: -0.02, direction: 'positive', importance_rank: 8, baseline_value: 30 }
  ]

  const positiveFeatures = features.filter(f => f.direction === 'positive').slice(0, 3)
  const negativeFeatures = features.filter(f => f.direction === 'negative').slice(0, 3)

  return {
    asset_id: assetId,
    model_type: 'base',
    prediction_value: 82.5,
    base_value: 85.0,
    features,
    summary: {
      top_positive: positiveFeatures,
      top_negative: negativeFeatures,
      explanation_text: `The health score of ${assetId} is primarily impacted by elevated discharge temperature (+15%) and vibration levels (+12%). Stable suction pressure and motor current are helping maintain overall health. Consider scheduling inspection for cooling system.`
    },
    timestamp: new Date().toISOString()
  }
}

function generateMockUnifiedPrediction(assetId: string): { data: UnifiedPrediction; isMock: boolean } {
  const foundation = generateMockModelPrediction(assetId, 'foundation')
  const base = generateMockModelPrediction(assetId, 'base')
  const perAsset = generateMockModelPrediction(assetId, 'per_asset')

  const avgHealth = (foundation.health_score + base.health_score + perAsset.health_score) / 3
  const confidenceSpread = Math.abs(foundation.confidence - perAsset.confidence)

  return {
    data: {
      asset_id: assetId,
      timestamp: new Date().toISOString(),
      predictions: {
        foundation,
        base,
        per_asset: perAsset
      },
      consensus: {
        health_score: Math.round(avgHealth * 10) / 10,
        confidence: (foundation.confidence + base.confidence + perAsset.confidence) / 3,
        agreement_level: confidenceSpread < 0.1 ? 'high' : confidenceSpread < 0.2 ? 'medium' : 'low',
        recommendation: avgHealth >= 85
          ? 'Asset is operating within optimal parameters. Continue regular monitoring.'
          : avgHealth >= 70
            ? 'Asset shows minor degradation. Schedule preventive maintenance within 2 weeks.'
            : 'Asset requires attention. Consider immediate inspection and corrective action.'
      },
      model_comparison: {
        best_model: 'per_asset',
        confidence_spread: confidenceSpread,
        divergence_warning: confidenceSpread > 0.15
      }
    },
    isMock: true
  }
}

/**
 * Get unified prediction with mock fallback
 */
export async function getUnifiedPredictionWithFallback(assetId: string): Promise<{
  data: UnifiedPrediction | null
  isMock: boolean
}> {
  // Check if mock data mode is enabled
  if (getUseMockData()) {
    console.log('Mock data mode enabled, using mock unified prediction')
    return generateMockUnifiedPrediction(assetId)
  }

  try {
    const data = await getUnifiedPrediction(assetId)
    return { data, isMock: false }
  } catch (error) {
    console.error('Unified prediction API failed:', error)
    // Return null when API fails - don't show mock data unless explicitly enabled
    return { data: null, isMock: false }
  }
}

/**
 * Get SHAP explanation with mock fallback
 */
export async function getSHAPExplanationWithFallback(
  assetId: string,
  modelType: ModelType = 'base'
): Promise<{
  data: SHAPExplanation | null
  isMock: boolean
}> {
  // Check if mock data mode is enabled
  if (getUseMockData()) {
    console.log('Mock data mode enabled, using mock SHAP explanation')
    return { data: generateMockSHAPExplanation(assetId), isMock: true }
  }

  try {
    const data = await getSHAPExplanation(assetId, modelType)
    return { data, isMock: false }
  } catch (error) {
    console.error('SHAP explanation API failed:', error)
    // Return null when API fails - don't show mock data unless explicitly enabled
    return { data: null, isMock: false }
  }
}

function generateMockHealthForecast(assetId: string, steps: number = 7): { data: HealthForecast; isMock: boolean } {
  const now = new Date()
  const currentHealth = 82
  const forecast: ForecastPoint[] = []

  // Generate daily forecast points (7 days default)
  for (let i = 0; i <= steps; i++) {
    const degradation = (i / steps) * 8 // 8% degradation over forecast period
    const noise = (Math.random() - 0.5) * 2
    const predictedHealth = currentHealth - degradation + noise

    forecast.push({
      timestamp: new Date(now.getTime() + i * 24 * 60 * 60 * 1000).toISOString(),
      predicted_health: Math.round(predictedHealth * 10) / 10,
      lower_bound: Math.round((predictedHealth - 5) * 10) / 10,
      upper_bound: Math.round((predictedHealth + 3) * 10) / 10,
      model_type: 'base'
    })
  }

  return {
    data: {
      asset_id: assetId,
      current_health: currentHealth,
      forecast,
      time_to_threshold: currentHealth > 70 ? {
        hours: Math.round(((currentHealth - 70) / 8) * steps * 24),
        threshold_type: 'warning'
      } : undefined
    },
    isMock: true
  }
}

/**
 * Get health forecast with mock fallback
 */
export async function getHealthForecastWithFallback(
  assetId: string,
  steps: number = 7,
  metric: string = 'anomaly_score'
): Promise<{
  data: HealthForecast | null
  isMock: boolean
}> {
  // Check if mock data mode is enabled
  if (getUseMockData()) {
    console.log('Mock data mode enabled, using mock health forecast')
    return generateMockHealthForecast(assetId, steps)
  }

  try {
    const data = await getHealthForecast(assetId, steps, metric)
    return { data, isMock: false }
  } catch (error) {
    console.error('Health forecast API failed:', error)
    // Return null when API fails - don't show mock data unless explicitly enabled
    return { data: null, isMock: false }
  }
}

// Export all functions as a namespace
export const predictionApi = {
  getPrediction,
  getUnifiedPrediction,
  getAllModelPredictions,
  getSHAPExplanation,
  getFeatureImportance,
  getHealthForecast,
  submitPredictionFeedback,
  getUnifiedPredictionWithFallback,
  getSHAPExplanationWithFallback,
  getHealthForecastWithFallback
}
